import type { Island, Vertex3 } from '@rta/rta-blueprint-library';

import type { ComponentController } from '@r-core/controller';

// Seams closer together (or to a slab end) than this collapse into one -- a sub-half-inch stretch is noise, not a cut.
const SEAM_EPSILON = 0.5;

export type JointCountertopLengths = {
	length: number;
	centerX: number;
	center: Vertex3;
	panelAxis: number;
	overhang: number;
	/** One stretch per span between seams, the outermost running to the far ends of the slab. Empty when the slab has no seam. */
	segments: Array<{ start: number; end: number }>;
};

export type GetJointCountertopLengthsParams = {
	controller: ComponentController;
	orderedGroup: Array<Island>;
	getAlongRowPosition: (island: Island) => number;
};

/**
 * The joint slab's whole length story (#304/#551): one total across every member's back-edge countertop
 * corners -- the exact geometry the per-island dimensions use, so the two can never disagree -- split at
 * each member's countertop breakpoints into row-wide stretches. The split is of the ROW, never per island.
 */
export const getJointCountertopLengths = (params: GetJointCountertopLengthsParams): JointCountertopLengths | null => {
	const { controller, orderedGroup, getAlongRowPosition } = params;
	const { kitchen, options } = controller;
	if (!options.showPanelOutsideDimensions) return null;

	let minX = Infinity;
	let maxX = -Infinity;
	let refCenter: Vertex3 | null = null;
	let panelAxis = 0;
	let overhang = 0;
	orderedGroup.forEach((island) => {
		const along = getAlongRowPosition(island);
		island.panels.getBackPanels().forEach((panel) => {
			const sidePoints = panel.getSidePoints({ includeCountertop: true, overhangOverride: options.showCountertops ? undefined : 0, translate: true, rotate: true });
			if (!sidePoints) return;
			sidePoints.corners.forEach((corner) => {
				minX = Math.min(minX, along + corner.x);
				maxX = Math.max(maxX, along + corner.x);
			});
			if (refCenter === null) {
				refCenter = sidePoints.center;
				panelAxis = panel.getOriginAngle();
				overhang = panel.countertopOverhang ?? 0;
			}
		});
	});
	if (refCenter === null || !Number.isFinite(minX)) return null;

	const seamPositions = orderedGroup
		.flatMap((island) => {
			const along = getAlongRowPosition(island);
			const [countertop] = kitchen.design.countertops.filterByIslandId(island.id);
			return (countertop?.breakpoints.children ?? []).map((breakpoint) => along + breakpoint.center);
		})
		.filter((position) => position > minX + SEAM_EPSILON && position < maxX - SEAM_EPSILON)
		.sort((a, b) => a - b)
		.filter((position, index, sorted) => index === 0 || position - sorted[index - 1] > SEAM_EPSILON);

	const edges = seamPositions.length === 0 ? [] : [minX, ...seamPositions, maxX];
	const segments = edges.slice(0, -1).map((start, index) => ({ start, end: edges[index + 1] }));

	return { length: maxX - minX, centerX: (minX + maxX) / 2, center: refCenter as Vertex3, panelAxis, overhang, segments };
};
